import axios from 'axios';
import { RefreshRequest } from '@/authApiClient';
import { jwtDecode } from 'jwt-decode';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { SELECTED_ORG_ID } from '@/configs/storageKeys';

interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const refreshAccessToken = async (
  refreshToken: string,
): Promise<RefreshTokenResponse> => {
  try {
    const response = await axios.post<RefreshTokenResponse>(
      `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/Validation/RefreshAccessToken`,
      {
        refreshToken: refreshToken,
      } as RefreshRequest,
      {
        headers: {
          'Content-Type': 'application/json',
          'ABRI-AUTH-CLIENT-ID': process.env.NEXT_PUBLIC_ABRI_AUTH_CLIENT_ID,
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return true;

    // Check if token expires in the next 5 minutes
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime + 300;
  } catch {
    return true;
  }
};

export const setupAxiosInterceptors = (
  axiosInstance: any,
  getTokens: () => { token: string; refreshToken: string | undefined } | null,
  onTokenRefresh: (token: string, refreshToken: string | undefined) => void,
) => {
  // Request interceptor to check token expiry before making requests
  axiosInstance.interceptors.request.use(
    async (config: any) => {
      const orgId = sessionStorage.getItem(SELECTED_ORG_ID);
      if (orgId) {
        config.headers['X-Organisation-Id'] = orgId;
      }

      const tokens = getTokens();
      if (!tokens) return config;

      if (
        isTokenExpired(tokens.token) &&
        !isRefreshing &&
        tokens.refreshToken
      ) {
        isRefreshing = true;

        try {
          const response = await refreshAccessToken(tokens.refreshToken);
          onTokenRefresh(response.token, response.refreshToken);
          processQueue(null, response.token);
          config.headers.Authorization = `Bearer ${response.token}`;
        } catch (error) {
          processQueue(error, null);
          throw error;
        } finally {
          isRefreshing = false;
        }
      }

      return config;
    },
    (error: any) => Promise.reject(error),
  );

  // Response interceptor to handle 401 errors
  axiosInstance.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const tokens = getTokens();
          if (!tokens || !tokens.refreshToken)
            throw new Error('No refresh token available');

          const response = await refreshAccessToken(tokens.refreshToken);
          onTokenRefresh(response.token, response.refreshToken);
          processQueue(null, response.token);
          originalRequest.headers.Authorization = `Bearer ${response.token}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Redirect to login if refresh fails
          window.location.href = PUBLIC_ROUTES.HOME;
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );
};
